import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import ProductSelectSize from './ProductSelectSize/ProductSelectSize';
import ProductUniqSize from './ProductUniqSize/ProductUniqSize';
import ProductSizeModal from './ProductSizeModal/ProductSizeModal';
import { useProductSize } from 'hooks/ProductPageV2/useProductSize';
import {
  PRODUCT_CUP_ATTRIBUTE,
  PRODUCT_SIZE_ATTRIBUTE,
} from 'settings/productV2';
import {
  ProductKind,
  ProductVariantsSkusType,
} from 'types/ProductPageV2/Products';
import { StockType } from 'types/ProductPageV2/ProductsStocks';
import ProductSizePicker from './ProductSizePicker/ProductSizePicker';
import {
  ItemType,
  Props as SelectProps,
} from 'components/Common/FieldCustom/SelectFieldCustom/SelectFieldCustom';

import styles from './ProductSizes.module.scss';

type Props = {
  skus: ProductVariantsSkusType;
  type: ProductKind;
  sizes: string[];
  cups: string[];
  size: string | undefined;
  setSize: (size: string) => void;
  cup: string | undefined;
  setCup: (cup: string) => void;
  open: boolean;
  closeModal: () => void;
  renderAddBasket: () => JSX.Element;
  stocks: StockType[] | undefined;
  stockIsLoaded: boolean;
  onSelectOpen?: () => void;
  SelectRenderComponent?: SelectProps['RenderComponent'];
  SelectRenderCustomProps?: Record<string, unknown>;
  classes?: {
    container?: string;
    containerInModal?: string;
  };
};

export default function ProductSizes({
  skus,
  type,
  sizes,
  cups,
  size,
  setSize,
  cup,
  setCup,
  open,
  closeModal,
  renderAddBasket,
  stocks,
  stockIsLoaded,
  onSelectOpen,
  classes,
  SelectRenderComponent,
  SelectRenderCustomProps,
}: Props): JSX.Element {
  const { t } = useTranslation(['productPage']);

  const sizeFilter =
    type === 2 && cup ? { key: PRODUCT_CUP_ATTRIBUTE, value: cup } : undefined;

  const cupFilter =
    type === 2 && size
      ? { key: PRODUCT_SIZE_ATTRIBUTE, value: size }
      : undefined;

  const { items: sizeItems } = useProductSize(
    skus,
    sizes,
    stocks,
    type,
    sizeFilter
  );

  const { items: cupItems } = useProductSize(
    skus,
    cups,
    stocks,
    type,
    cupFilter
  );

  function renderSelect(
    items: ItemType[],
    selectedItem: ItemType['value'] | undefined,
    setItem: (value: string) => void,
    placeholder: string,
    forModal: boolean
  ) {
    return (
      <ProductSelectSize
        items={items}
        size={selectedItem}
        setSize={setItem}
        placeholder={placeholder}
        showLoader={!stockIsLoaded}
        onSelectOpen={onSelectOpen}
        SelectRenderComponent={SelectRenderComponent}
        SelectRenderCustomProps={{
          ...SelectRenderCustomProps,
          forModal: forModal,
        }}
        classes={{
          openedContainer: forModal ? styles.selectInModal : undefined,
        }}
      />
    );
  }

  function renderFilters(forModal: boolean): JSX.Element {
    return (
      <div
        className={classNames(
          styles.filters,
          !forModal && classes?.container,
          forModal && classes?.containerInModal
        )}
      >
        {type === 0 && stocks && <ProductUniqSize stock={stocks[0]} />}
        {forModal && type === 1 ? (
          <ProductSizePicker items={sizeItems} size={size} setSize={setSize} />
        ) : (
          type > 0 &&
          renderSelect(
            sizeItems,
            size,
            setSize,
            t('LABEL_CHOOSE_SIZE'),
            forModal
          )
        )}
        {type > 1 &&
          renderSelect(cupItems, cup, setCup, t('LABEL_CHOOSE_CUP'), forModal)}
      </div>
    );
  }

  return (
    <>
      {type > 0 && (
        <ProductSizeModal
          isOpen={open}
          closeModal={closeModal}
          type={type}
          content={renderFilters(true)}
          renderAddBasket={renderAddBasket}
        />
      )}
      {renderFilters(false)}
    </>
  );
}
